import { isDrawableByPdfMake } from "../../infrastructure/documents/pdf-image.js";
import type { RequestContext } from "../context/request-context.js";
import type { MeReportRepository } from "../../repositories/operational-repositories.js";
import { isMeHighTemperature, meEffectiveThickness, normalizeMeThermalFields, type MeReport } from "../../domain/engineering/me-report-domain.js";
import type { AvrSourceKind, AvrSourceReport, AvrThicknessReading } from "../../domain/engineering/avr-report-domain.js";

/**
 * Fontes de espessura do AVR. Só existem duas: Relatório ME e Relatório
 * B-Scan. Checklist, campos do Equipment, Recommendation, RI ou digitação
 * direta não são fontes — não há adaptador para eles, de propósito.
 */
export interface AvrThicknessSourceAdapter {
  readonly kind: AvrSourceKind;
  /** Relatórios deste tipo para o equipamento — nunca de outro equipamento. */
  listReports(context: RequestContext, clientId: string, equipmentId: string): Promise<AvrSourceReport[]>;
  /** Leituras de espessura de UM relatório, cada uma com a sua origem. */
  readingsOf(context: RequestContext, clientId: string, reportId: string): Promise<AvrThicknessReading[]>;
  /** Croqui do relatório de origem (data URL) para reaproveitar na AVR — nulo quando não há. */
  croquiOf?(context: RequestContext, clientId: string, reportId: string): Promise<string | null>;
}

function civilDate(raw: unknown): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(raw ?? ""));
  return m ? m[0] : "";
}

function toNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  const parsed = Number(String(value).replace(",", "."));
  return Number.isFinite(parsed) ? parsed : null;
}

/** Fonte aprovada para avaliação definitiva: o ME validado ou assinado. */
export function isMeApproved(report: Pick<MeReport, "status">): boolean {
  return report.status === "VALIDATED" || report.status === "SIGNED";
}

/** Relatório ME → leituras por ponto (CML), com relatório, revisão, data e ponto preservados. */
export class MeAvrSourceAdapter implements AvrThicknessSourceAdapter {
  readonly kind: AvrSourceKind = "ME";

  constructor(private readonly meReportRepo: MeReportRepository) {}

  private toSource(report: MeReport): AvrSourceReport {
    return {
      kind: "ME",
      reportId: String(report.id),
      reportNumber: report.reportNumber,
      revision: report.revision,
      testDate: civilDate(report.testDate || report.periodEnd || report.periodStart),
      status: report.status,
      approved: isMeApproved(report),
      equipmentId: String(report.equipmentId ?? ""),
      equipmentTag: report.equipmentTag,
      osWorkOrderId: report.osWorkOrderId ?? null,
      osNumber: report.osNumber || "",
      osSubWorkOrderId: report.osSubWorkOrderId ?? null,
      subOsNumber: report.subOsNumber || "",
      hasCroqui: typeof report.croquiImage === "string" && report.croquiImage.startsWith("data:image")
    };
  }

  async croquiOf(context: RequestContext, _clientId: string, reportId: string): Promise<string | null> {
    const report = await this.meReportRepo.findById(context, reportId);
    const image = report?.croquiImage;
    return isDrawableByPdfMake(image) ? image : null;
  }

  async listReports(context: RequestContext, clientId: string, equipmentId: string): Promise<AvrSourceReport[]> {
    const reports = await this.meReportRepo.listByClient(context, clientId);
    return reports
      .filter(r => String(r.equipmentId ?? "") === equipmentId)
      .map(r => this.toSource(r))
      .filter(s => s.testDate !== "")
      .sort((a, b) => a.testDate.localeCompare(b.testDate));
  }

  async readingsOf(context: RequestContext, _clientId: string, reportId: string): Promise<AvrThicknessReading[]> {
    const report = await this.meReportRepo.findById(context, reportId);
    if (!report) return [];
    // Relatório gravado antes dos campos de alta temperatura: traz o texto
    // legado para o modelo atual antes de decidir qual espessura vale.
    normalizeMeThermalFields(report);
    const source = this.toSource(report);
    const readings: AvrThicknessReading[] = [];
    // Em medição de ALTA TEMPERATURA a espessura que alimenta taxa de
    // corrosão e vida remanescente é a Ea (MÉTODO A — RESULTADO APROXIMADO),
    // nunca a Emq: a leitura feita quente não é a espessura aproximada do
    // componente, e usá-la aqui produziria uma vida remanescente otimista a
    // partir de um número que ninguém corrigiu. Um ponto em alta temperatura
    // SEM Ea não entra no cálculo — `meEffectiveThickness` devolve null e ele
    // é descartado como qualquer outro ponto sem espessura, em vez de entrar
    // disfarçado de medida boa.
    const highTemperature = isMeHighTemperature(report);
    for (const point of report.points || []) {
      const thickness = toNumber(meEffectiveThickness(report, point));
      const pointId = String(point.id || "").trim();
      if (thickness === null || !pointId) continue;
      readings.push({
        sourceKind: "ME",
        reportId: source.reportId,
        reportNumber: source.reportNumber,
        revision: source.revision,
        date: source.testDate,
        pointId,
        component: point.component || "",
        location: point.location || "",
        nominalMm: toNumber(point.nominalThickness),
        minMm: toNumber(point.minThickness),
        thicknessMm: thickness,
        hotMeasuredThicknessMm: highTemperature ? toNumber(point.actualThickness) : null,
        temperatureC: highTemperature ? report.measurementTemperatureC : null,
        // AVR → medição → Ea → FC_A → Procedimento (MÉTODO A), lidos do retrato gravado.
        correctionFactor: highTemperature ? point.hotCorrection?.correctionFactor ?? null : null,
        procedureCode: highTemperature ? point.hotCorrection?.procedureCode || null : null,
        // Emq para auditoria (mesmo valor de hotMeasuredThicknessMm) e o
        // procedureCode também como calibrationId — no Método A é a mesma
        // origem controlada que substituiu a calibração do Método B.
        ematMm: highTemperature ? toNumber(point.actualThickness) : null,
        calibrationId: highTemperature ? point.hotCorrection?.procedureCode || null : null,
        measurementKey: point.key || null,
        approved: source.approved
      });
    }
    return readings;
  }
}

/**
 * Relatório B-Scan. O INSPECT IA ainda não possui um módulo de Relatório
 * B-Scan com resultados por região gravados no app real — este adaptador
 * existe para que o AVR já tenha o contrato da segunda fonte, e devolve
 * VAZIO até que esse relatório exista. Nenhum dado é inventado aqui.
 */
export class BscanAvrSourceAdapter implements AvrThicknessSourceAdapter {
  readonly kind: AvrSourceKind = "BSCAN";

  constructor(private readonly loader?: {
    listReports: (context: RequestContext, clientId: string, equipmentId: string) => Promise<AvrSourceReport[]>;
    readingsOf: (context: RequestContext, clientId: string, reportId: string) => Promise<AvrThicknessReading[]>;
  }) {}

  async listReports(context: RequestContext, clientId: string, equipmentId: string): Promise<AvrSourceReport[]> {
    return this.loader ? this.loader.listReports(context, clientId, equipmentId) : [];
  }

  async readingsOf(context: RequestContext, clientId: string, reportId: string): Promise<AvrThicknessReading[]> {
    return this.loader ? this.loader.readingsOf(context, clientId, reportId) : [];
  }
}
